import { supabase, type User } from './supabase'

export type MissionEventType =
  | 'COMPLETE_CLASS'
  | 'PUBLISH_CONTENT'
  | 'INVITE_USER'
  | 'SELL_SERVICE'
  | 'FIRST_LOGIN'
  | 'COMPLETE_PROFILE'

interface MissionRow {
  id: string
  title: string
  type: MissionEventType
  target: number
  xp_reward: number
  is_active: boolean
}

interface UserMissionRow {
  mission_id: string
  progress: number
  completed: boolean
  completed_at: string | null
  xp_claimed?: boolean
}

let supportsXpClaimedCache: boolean | null = null

const calculateLevel = (xp: number): number => {
  const thresholds = [0, 100, 250, 500, 1000, 2000, 4000, 8000, 16000, 32000]
  let level = 1
  for (let i = 0; i < thresholds.length; i += 1) {
    if (xp >= thresholds[i]) level = i + 1
  }
  return level
}

const isMissingColumnError = (message?: string): boolean =>
  Boolean(message?.toLowerCase().includes("could not find the 'xp_claimed' column"))

const checkXpClaimedSupport = async (): Promise<boolean> => {
  if (supportsXpClaimedCache !== null) return supportsXpClaimedCache
  const { error } = await supabase.from('user_missions').select('xp_claimed').limit(1)
  if (error && isMissingColumnError(error.message)) {
    supportsXpClaimedCache = false
    return false
  }
  supportsXpClaimedCache = true
  return true
}

const getProfileProgress = (user: User): number => {
  const checks = [user.display_name, user.bio, user.avatar_url]
  const completedChecks = checks.filter((value) => Boolean(value && `${value}`.trim().length > 0)).length
  return completedChecks
}

const incrementUserXp = async (userId: string, amount: number): Promise<boolean> => {
  if (amount <= 0) return true

  const { data, error } = await supabase
    .from('users')
    .select('xp, points')
    .eq('id', userId)
    .single()

  if (error || !data) return false

  const nextXp = (data.xp || 0) + amount
  const nextPoints = (data.points || 0) + amount
  const nextLevel = calculateLevel(nextXp)

  const { error: updateError } = await supabase
    .from('users')
    .update({
      xp: nextXp,
      points: nextPoints,
      level: nextLevel,
      updated_at: new Date().toISOString(),
    })
    .eq('id', userId)

  return !updateError
}

export async function processMissionEvent(
  userId: string,
  eventType: MissionEventType,
  options?: { increment?: number; profile?: User }
): Promise<{ awardedXp: number; completedMissions: string[] }> {
  const increment = Math.max(1, options?.increment ?? 1)
  const { data: missions, error: missionError } = await supabase
    .from('missions')
    .select('id, title, type, target, xp_reward, is_active')
    .eq('type', eventType)
    .eq('is_active', true)

  if (missionError || !missions || missions.length === 0) {
    return { awardedXp: 0, completedMissions: [] }
  }

  const missionIds = missions.map((mission) => mission.id)
  let progressRows: UserMissionRow[] | null = null
  let progressError: { message?: string } | null = null

  {
    const response = await supabase
      .from('user_missions')
      .select('mission_id, progress, completed, completed_at, xp_claimed')
      .eq('user_id', userId)
      .in('mission_id', missionIds)

    progressRows = (response.data as UserMissionRow[] | null) ?? null
    progressError = response.error
  }

  if (progressError && isMissingColumnError(progressError.message)) {
    supportsXpClaimedCache = false
    const fallback = await supabase
      .from('user_missions')
      .select('mission_id, progress, completed, completed_at')
      .eq('user_id', userId)
      .in('mission_id', missionIds)
    progressRows = (fallback.data as UserMissionRow[] | null) ?? null
    progressError = fallback.error
  }

  if (progressError) {
    progressRows = []
  }

  const progressMap = new Map<string, UserMissionRow>(
    ((progressRows || []) as UserMissionRow[]).map((row) => [row.mission_id, row])
  )
  const supportsXpClaimed = await checkXpClaimedSupport()

  let awardedXp = 0
  const completedMissions: string[] = []

  for (const mission of missions as MissionRow[]) {
    const current = progressMap.get(mission.id)
    const currentProgress = current?.progress || 0
    const currentCompleted = current?.completed || false
    const target = Math.max(1, mission.target || 1)

    let nextProgress = currentProgress
    if (eventType === 'FIRST_LOGIN') {
      nextProgress = target
    } else if (eventType === 'COMPLETE_PROFILE' && options?.profile) {
      nextProgress = Math.min(target, getProfileProgress(options.profile))
    } else if (!currentCompleted) {
      nextProgress = Math.min(target, currentProgress + increment)
    }

    const nextCompleted = nextProgress >= target
    const justCompleted = nextCompleted && !currentCompleted
    const canClaimByFlag = supportsXpClaimed ? !(current?.xp_claimed ?? false) : justCompleted
    const shouldAward = nextCompleted && canClaimByFlag

    const upsertPayload: Record<string, unknown> = {
      user_id: userId,
      mission_id: mission.id,
      progress: nextProgress,
      completed: nextCompleted,
      completed_at: nextCompleted ? current?.completed_at || new Date().toISOString() : null,
      updated_at: new Date().toISOString(),
    }

    if (supportsXpClaimed) {
      upsertPayload.xp_claimed = current?.xp_claimed ?? false
    }

    const { error: upsertError } = await supabase
      .from('user_missions')
      .upsert(upsertPayload, { onConflict: 'user_id,mission_id' })

    if (upsertError) {
      continue
    }

    if (shouldAward) {
      const awardOk = await incrementUserXp(userId, mission.xp_reward)
      if (!awardOk) continue

      awardedXp += mission.xp_reward
      completedMissions.push(mission.title)

      if (supportsXpClaimed) {
        await supabase
          .from('user_missions')
          .update({ xp_claimed: true, updated_at: new Date().toISOString() })
          .eq('user_id', userId)
          .eq('mission_id', mission.id)
      }
    }
  }

  return { awardedXp, completedMissions }
}

export async function syncCoreMissions(user: User): Promise<{ awardedXp: number }> {
  const firstLogin = await processMissionEvent(user.id, 'FIRST_LOGIN')
  const profile = await processMissionEvent(user.id, 'COMPLETE_PROFILE', { profile: user })
  return { awardedXp: firstLogin.awardedXp + profile.awardedXp }
}
